import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Stack,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import { listHolidays, createHoliday, deleteHoliday } from '../services/holidays';

const HolidaysPage = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await listHolidays();
      setHolidays(data || []);
    } catch (e) {
      setSnack({ open: true, msg: "Erreur lors du chargement des jours fériés.", severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!date) {
      setSnack({ open: true, msg: 'Date requise', severity: 'warning' });
      return;
    }
    try {
      const payload = { date, name };
      await createHoliday(payload);
      setDate('');
      setName('');
      setSnack({ open: true, msg: 'Jour férié ajouté.', severity: 'success' });
      fetchData();
    } catch (e) {
      const msg = e?.response?.data?.message || 'Erreur lors de la création.';
      setSnack({ open: true, msg, severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce jour férié ?')) return;
    try {
      await deleteHoliday(id);
      setSnack({ open: true, msg: 'Supprimé.', severity: 'success' });
      fetchData();
    } catch (e) {
      setSnack({ open: true, msg: 'Erreur lors de la suppression.', severity: 'error' });
    }
  };

  const columns = [
    { field: 'date', headerName: 'Date', width: 150, headerClassName: 'super-app-theme--header' },
    { field: 'name', headerName: 'Nom', flex: 1, headerClassName: 'super-app-theme--header' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 1 }}>
          <Tooltip title="Supprimer">
            <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Gestion des jours fériés
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Ajouter un jour férié
          </Typography>
          <Box component="form" onSubmit={handleAdd} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
            <TextField
              label="Nom (optionnel)"
              placeholder="Ex: Fête nationale"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <Button type="submit" variant="contained" disabled={loading}>
              Ajouter
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Liste des jours fériés</Typography>
            <Button onClick={fetchData} disabled={loading} variant="outlined">Actualiser</Button>
          </Stack>
          <DataGrid
            rows={holidays || []}
            columns={columns}
            getRowId={(r) => r.id}
            pageSize={10}
            autoPageSize={false}
            getRowHeight={() => 80}
            sx={{
              '& .super-app-theme--header': {
                backgroundColor: '#1976d2',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
              },
              '& .MuiDataGrid-root': {
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#1976d2',
                fontSize: '14px',
                fontWeight: 600,
                color: 'white',
                borderBottom: '2px solid #1565c0',
              },
              '& .MuiDataGrid-columnHeader': {
                '&:focus': {
                  outline: 'none',
                },
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              },
              '& .MuiDataGrid-cell': {
                fontSize: '13px',
                padding: '16px 8px',
                borderBottom: '1px solid #f0f0f0',
                '&:focus': {
                  outline: 'none',
                },
              },
              '& .MuiDataGrid-row': {
                '&:nth-of-type(even)': {
                  backgroundColor: '#fafafa',
                },
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                },
                '&.Mui-selected': {
                  backgroundColor: '#e3f2fd',
                },
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #e0e0e0',
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-pagination': {
                '& .MuiIconButton-root': {
                  borderRadius: '4px',
                },
              },
            }}
          />
        </CardContent>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HolidaysPage;
